"use client";

import type { IImage } from "@components/types/media";
import type { VariantProps } from "class-variance-authority";
import type { StaticImageData } from "next/image";

import { env } from "@/env";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import NextImage from "next/image";
import { useEffect, useRef, useState } from "react";

// If changed, update next.config.mjs
const SUPPORTED_NEXT_IMAGE_HOSTS = [
  "storage.googleapis.com",
  env.NEXT_PUBLIC_CDN_DOMAIN,
  env.NEXT_PUBLIC_MEDIA_SERVER_URL,
]
  .filter(url => !!url)
  .map(url => url?.replace("https://", ""));

const imageVariants = cva("w-full h-full transition-all duration-700 ease-out-slow transform-gpu", {
  variants: {
    fit: {
      cover: "object-cover",
      contain: "object-contain",
    },
    isLoading: {
      true: "opacity-0 scale-[1.025]",
      false: "",
    },
  },
  defaultVariants: {
    fit: "cover",
    isLoading: true,
  },
});

export interface ImageProps extends IImage, Pick<VariantProps<typeof imageVariants>, "fit"> {
  className?: string;
  external?: boolean;
  onReveal?: () => void;
}

/**
 * @deprecated Use Image from next/image instead. See getImageProps() for more information and examples.
 */
const Image = (props: ImageProps) => {
  const {
    className,
    onLoad: onLoadFromProps,
    onReveal,
    resource,
    fit,
    fill,
    src: srcFromProps,
    alt: altFromProps,
    loading = "lazy",
    quality = 95,
    width,
    height,
    sizes = "100vw",
    contained: _, // this cannot be passed with rest of the props to next image
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(loading === "lazy");
  const [isReveal, setIsReveal] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  let _width = width;
  let _height = height;
  let alt: null | string | undefined = altFromProps;
  let src: StaticImageData | string = srcFromProps || "";
  let external = true;

  if (resource && typeof resource !== "string" && typeof resource !== "number") {
    // Payload media
    const { width: resourceWidth, height: resourceHeight, url: resourceUrl, alt: altFromResource } = resource;

    if (resourceWidth !== null) {
      _width = resourceWidth;
    }
    if (resourceHeight !== null) {
      _height = resourceHeight;
    }
    alt = altFromProps || altFromResource;
    if (typeof resourceUrl === "string") {
      src = resourceUrl;
    }

    let host = "";
    try {
      host = new URL(src).host;
    } catch (error) {
      // Nothing to do, invalid url.
    }

    if (host && (fill || (_width && _height)) && SUPPORTED_NEXT_IMAGE_HOSTS.includes(host)) {
      external = false;
    }
  }

  if (resource && typeof resource === "string") {
    src = resource;
  }

  if (src.startsWith("/")) {
    external = false;
  }

  useEffect(() => {
    if (imageRef.current) {
      const IO = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsReveal(true);
            IO.disconnect();
          }
        });
      });
      if (imageRef.current.complete) {
        setIsLoading(false);
        if (typeof onReveal === "function") {
          onReveal();
        }
      }
      IO.observe(imageRef.current);

      return () => {
        IO.disconnect();
      };
    }
  }, [external, onReveal]);

  return (
    src &&
    (!external ? (
      <NextImage
        alt={alt || ""}
        className={cn(imageVariants({ fit, isLoading: !(isReveal && !isLoading), className }))}
        height={!fill ? _height : undefined}
        loading={loading}
        onLoad={event => {
          window.requestAnimationFrame(() => {
            setIsLoading(false);
          });
          if (typeof onLoadFromProps === "function") {
            onLoadFromProps(event);
          }
        }}
        onTransitionEnd={() => {
          if (typeof onReveal === "function") {
            onReveal();
          }
        }}
        quality={quality}
        ref={imageRef}
        sizes={sizes}
        src={src}
        width={!fill ? _width : undefined}
        {...rest}
      />
    ) : (
      <img
        alt={alt || ""}
        className={cn(imageVariants({ fit, isLoading: !isReveal && isLoading, className }))}
        height={_height}
        loading={loading}
        onLoad={event => {
          setIsLoading(false);
          if (typeof onLoadFromProps === "function") {
            onLoadFromProps(event);
          }
        }}
        onTransitionEnd={() => {
          if (typeof onReveal === "function") {
            onReveal();
          }
        }}
        ref={imageRef}
        sizes={sizes}
        src={src}
        width={_width}
        {...rest}
      />
    ))
  );
};

Image.displayName = "Image";

export { Image };
